import React from 'react';
import { Plus, Filter } from 'lucide-react';

export type PageSizeOption = 10 | 20 | 50 | 'all';

interface EntityListLayoutProps {
  title: string;
  /** Add-button label. The button is hidden when `onAdd` is not provided. */
  addButtonLabel?: string;
  onAdd?: () => void;
  headerActions?: React.ReactNode;
  toolbarActions?: React.ReactNode;
  entriesCount: number;
  onOpenFilter: () => void;
  currentPageSize?: PageSizeOption;
  onPageSizeChange?: (size: PageSizeOption) => void;
  isLoading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
}

export const EntityListLayout: React.FC<EntityListLayoutProps> = ({
  title,
  addButtonLabel,
  onAdd,
  headerActions,
  toolbarActions,
  entriesCount,
  onOpenFilter,
  currentPageSize,
  onPageSizeChange,
  isLoading = false,
  loadingText = 'Loading...',
  children,
}) => {
  const pageSizeOptions: PageSizeOption[] = [10, 20, 50, 'all'];
  const showPageSizeControls =
    entriesCount >= 10 &&
    currentPageSize !== undefined &&
    onPageSizeChange !== undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{loadingText}</div>
      </div>
    );
  }

  return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-neutral-900">{title}</h1>
          <div className="flex items-center gap-3">
            {headerActions}
            {onAdd && (
              <button
                onClick={onAdd}
                className="flex items-center gap-2 text-white hover:opacity-90 transition-opacity"
                style={{
                  height: '38px',
                  borderRadius: '6px',
                  backgroundColor: '#404040',
                  border: '1px solid #404040',
                  padding: '0 16px',
                  boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
                }}
              >
                <Plus size={20} color="#EAECF0" strokeWidth={1.67} aria-hidden="true" />
                <span
                  style={{
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#EAECF0',
                  }}
                >
                  {addButtonLabel}
                </span>
              </button>
            )}
          </div>
       </div>

      <div className="mb-6 flex items-center justify-between gap-3">
        <div>{toolbarActions}</div>
        <div className="flex items-center gap-3">
          <p
            style={{
              fontFamily: 'Oxygen',
              fontSize: '14px',
              fontWeight: 400,
              color: '#667085',
            }}
          >
            {entriesCount} entries
          </p>
          {showPageSizeControls && (
            <div className="flex items-center gap-2">
              {pageSizeOptions.map((pageSizeOption) => {
                const isSelected = currentPageSize === pageSizeOption;

                return (
                  <button
                    key={pageSizeOption}
                    type="button"
                    onClick={() => onPageSizeChange(pageSizeOption)}
                    aria-pressed={isSelected}
                    className="transition-colors"
                    style={{
                      minWidth: pageSizeOption === 'all' ? '46px' : '38px',
                      height: '38px',
                      borderRadius: '6px',
                      backgroundColor: isSelected ? '#404040' : '#F6F5F3',
                      border: `1px solid ${isSelected ? '#404040' : '#D0D5DD'}`,
                      padding: '0 12px',
                      fontFamily: 'Oxygen',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: isSelected ? '#EAECF0' : '#344054',
                    }}
                  >
                    {pageSizeOption}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={onOpenFilter}
            className="flex items-center gap-2 hover:bg-neutral-100 transition-colors"
            style={{
              width: '90px',
              height: '38px',
              borderRadius: '6px',
              backgroundColor: '#F6F5F3',
              border: '1px solid #D0D5DD',
              padding: '0 16px',
            }}
          >
            <Filter size={20} color="#344054" strokeWidth={1.67} aria-hidden="true" />
            <span
              style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#344054',
              }}
            >
              Filter
            </span>
          </button>
        </div>
      </div>

      {children}
    </div>
  );
};

export default EntityListLayout;
